/**
 * RoleProtectedRoute - Route guard using extended UserRole hierarchy
 * Complements the existing ProtectedRoute for the new role system
 */
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { UserRole, hasMinimumRole, mapAppRoleToUserRole, ROLE_DASHBOARD_PATHS } from '@/types/roles';
import { hasPermission, Permission } from '@/config/permissions';
import { Loader2 } from 'lucide-react';

interface RoleProtectedRouteProps {
  children: React.ReactNode;
  minRole?: UserRole;
  allowedRoles?: UserRole[];
  permissions?: Permission[];
  requireAll?: boolean;
}

export function RoleProtectedRoute({
  children,
  minRole,
  allowedRoles,
  permissions: requiredPermissions,
  requireAll = false,
}: RoleProtectedRouteProps) {
  const { isAuthenticated, isLoading, role, authInfo } = useAuth();
  const location = useLocation();
  const lang = location.pathname.split('/')[1] || 'en';

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Verifying access...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={`/${lang}/auth`} state={{ from: location }} replace />;
  }

  const userRole = mapAppRoleToUserRole(role || 'candidate', authInfo?.internal_role);

  // Check minimum role
  if (minRole && !hasMinimumRole(userRole, minRole)) {
    return <Navigate to={`/${lang}/unauthorized`} replace />;
  }

  // Check allowed roles
  if (allowedRoles && !allowedRoles.includes(userRole)) {
    return <Navigate to={`/${lang}/unauthorized`} replace />;
  }

  // Check permissions
  if (requiredPermissions && requiredPermissions.length > 0) {
    const hasAccess = requireAll
      ? requiredPermissions.every(p => hasPermission(userRole, p))
      : requiredPermissions.some(p => hasPermission(userRole, p));
    if (!hasAccess) {
      return <Navigate to={`/${lang}/unauthorized`} replace />;
    }
  }

  return <>{children}</>;
}

export default RoleProtectedRoute;
